import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  BookOpen, CalendarDays, ChevronRight, CircleCheck, CircleX, Crown, FileQuestion, LayoutDashboard, Medal, Newspaper,
  NotebookPen, Target, TrendingUp, Trophy,
} from 'lucide-react';
import { articleService, dashboardService, questionService, examService } from '../services/api';
import HabitCard from '../components/HabitCard';
import LevelCard from '../components/LevelCard';
import { useCourse } from '../contexts/CourseContext';
import { AppShell, Card, Loader, PageHeader, ScoreCard, SectionTitle } from '../components/ui';
import { tierIcon, tierOf } from '../utils/gamification';
import './Home.css';

function Home() {
  const navigate = useNavigate();
  const { hash } = useLocation();
  const { courseSlug } = useParams();
  const { currentCourse, selectCourse } = useCourse();
  const [questionCount, setQuestionCount] = useState(0);
  const [completedExams, setCompletedExams] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  // Rotas antigas (/home) caem no curso padrão, como os demais redirecionamentos legados
  const slug = courseSlug || 'software-architecture';
  const buildPath = (path) => `/curso/${slug}${path}`;

  useEffect(() => {
    selectCourse(slug);
  }, [slug, selectCourse]);

  useEffect(() => {
    articleService.highlights(slug, 3).then(setArticles).catch(() => setArticles([]));
  }, [slug]);

  useEffect(() => {
    const loadData = async () => {
      try {
        const [count, exams, dash] = await Promise.all([
          questionService.countQuestions(slug),
          examService.getCompletedExams(slug),
          dashboardService.getDashboard(slug).catch(() => null),
        ]);
        setQuestionCount(count);
        setCompletedExams(exams);
        setDashboard(dash);
      } catch (error) {
        console.error('Erro ao carregar dados:', error);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [slug]);

  // "Ver minha semana" (Estudo) chega com #sua-semana: rola até a seção depois de carregar
  useEffect(() => {
    if (loading || !hash) return;
    document.getElementById(hash.slice(1))?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [loading, hash]);

  if (loading) {
    return (
      <AppShell section="Visão geral" courseName={currentCourse?.name}>
        <Loader />
      </AppShell>
    );
  }

  const passedCount = completedExams.filter(e => e.passed).length;
  const average = completedExams.length
    ? Math.round(completedExams.reduce((acc, e) => acc + (Number(e.score) || 0), 0) / completedExams.length)
    : null;
  const passing = Number(completedExams[0]?.passingPercentage ?? 61);
  const gamification = dashboard?.gamification;

  const stats = [
    { icon: FileQuestion, value: questionCount, label: 'Questões', hint: 'no banco' },
    { icon: Target, value: completedExams.length, label: 'Simulados', hint: 'realizados' },
    { icon: Trophy, value: passedCount, label: 'Aprovações', hint: 'nos simulados', tone: 'lime' },
    {
      icon: TrendingUp,
      value: average != null ? `${average}%` : '—',
      label: 'Média',
      hint: 'de acerto',
      tone: average != null && average < passing ? 'danger' : 'navy',
    },
  ];

  const shortcuts = [
    { icon: BookOpen, title: 'Modo estudo', desc: 'Tópicos e aulas por fase', path: '/estudo' },
    { icon: Target, title: 'Simulados', desc: 'Histórico e evolução', path: '/simulados' },
    { icon: NotebookPen, title: 'Caderno de erros', desc: 'Revise o que errou', path: '/erros' },
  ];

  return (
    <AppShell section="Visão geral" courseName={currentCourse?.name}>
      <div className="ss-page">
        <PageHeader
          icon={LayoutDashboard}
          eyebrow={currentCourse?.name}
          title="Visão geral"
          subtitle="Seu desempenho nos simulados e atalhos para continuar estudando."
        />

        <ScoreCard
          className="home-score"
          tour="level"
          stripe={!!gamification}
          top={gamification && <LevelCard data={gamification} embedded />}
          items={stats}
        />

        <div className="home-actions" data-tour="atalhos">
          {shortcuts.map(a => (
            <button key={a.path} type="button" className="home-action" onClick={() => navigate(buildPath(a.path))}>
              <span className="home-action__icon"><a.icon size={18} aria-hidden="true" /></span>
              <span className="home-action__text">
                <strong>{a.title}</strong>
                <span>{a.desc}</span>
              </span>
              <ChevronRight size={16} className="home-action__go" aria-hidden="true" />
            </button>
          ))}
        </div>

        <section className="ss-section" data-tour="desempenho">
          <SectionTitle
            icon={TrendingUp}
            aside={(
              <button type="button" className="home-seeall" onClick={() => navigate(buildPath('/simulados'))}>
                Ver simulados <ChevronRight size={15} aria-hidden="true" />
              </button>
            )}
          >
            Desempenho
          </SectionTitle>
          <div className="home-perf">
            <Card padding="none" className="home-panel">
              <span className="home-panel__eyebrow">
                Últimos simulados{completedExams.length > 5 ? ' · últimos 5' : ''}
              </span>
              {completedExams.length === 0 ? (
                <p className="home-panel__empty">Faça o primeiro simulado para acompanhar sua evolução aqui.</p>
              ) : (
                <div className="home-exams">
                  {completedExams.slice(0, 5).map(exam => (
                    <button key={exam.id} type="button" className="home-exam" onClick={() => navigate(buildPath(`/results/${exam.id}`))}>
                      <span className={`home-exam__icon ${exam.passed ? 'is-pass' : 'is-fail'}`}>
                        {exam.passed ? <CircleCheck size={18} aria-hidden="true" /> : <CircleX size={18} aria-hidden="true" />}
                      </span>
                      <span className="home-exam__text">
                        <span className="home-exam__title">{exam.title}</span>
                        <span className="home-exam__date">
                          {new Date(exam.completedAt).toLocaleDateString('pt-BR')} · {exam.passed ? 'aprovado' : 'reprovado'}
                        </span>
                      </span>
                      <span className={`home-exam__score ${exam.passed ? 'is-pass' : 'is-fail'}`}>
                        {Math.round(Number(exam.score) || 0)}%
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </Card>

            {gamification && (
              <Card padding="none" className="home-panel">
                <span className="home-panel__eyebrow">Maestria por tópico</span>
                <Mastery counts={gamification.masteryCounts} topics={gamification.masteryTop} />
              </Card>
            )}
          </div>
        </section>

        {dashboard?.activity && (
          <section className="ss-section" id="sua-semana" data-tour="sua-semana">
            <SectionTitle icon={CalendarDays}>Sua semana</SectionTitle>
            <HabitCard activity={dashboard.activity} goals={dashboard.goals} />
          </section>
        )}

        {articles.length > 0 && (
          <section className="ss-section">
            <SectionTitle
              icon={Newspaper}
              aside={(
                <button type="button" className="home-seeall" onClick={() => navigate(`/artigos?curso=${slug}`)}>
                  Ver todos <ChevronRight size={15} aria-hidden="true" />
                </button>
              )}
            >
              Artigos recentes
            </SectionTitle>
            <div className="home-articles">
              {articles.map(a => (
                <button key={a.id} type="button" className="home-article" onClick={() => navigate(`/artigos/${a.slug}`)}>
                  <span className="home-article__icon"><Newspaper size={19} aria-hidden="true" /></span>
                  <span className="home-article__text">
                    <span className="home-article__title">{a.title}</span>
                    <span className="home-article__by">{a.author?.name} · {a.readingMinutes} min</span>
                    {a.tags?.[0] && <span className="home-article__tag">{a.tags[0].name}</span>}
                  </span>
                  <ChevronRight size={16} aria-hidden="true" className="home-article__go" />
                </button>
              ))}
            </div>
          </section>
        )}
      </div>
    </AppShell>
  );
}

const TIER_COUNTERS = [
  { key: 'gold', icon: Crown },
  { key: 'silver', icon: Medal },
  { key: 'bronze', icon: Medal },
];

/** Contagem ouro/prata/bronze e lista compacta dos tópicos com maior maestria. */
function Mastery({ counts, topics }) {
  return (
    <>
      <div className="home-tiers">
        {TIER_COUNTERS.map(({ key, icon: Icon }) => {
          const tier = tierOf(key);
          return (
            <div key={key} className="home-tier" style={{ background: tier.bg, color: tier.fg }}>
              <Icon size={20} aria-hidden="true" />
              <strong>{counts?.[key] || 0}</strong>
              <span>{tier.label}</span>
            </div>
          );
        })}
      </div>
      {topics?.length > 0 && (
        <div className="home-mastery">
          {topics.map(t => {
            const tier = tierOf(t.tier);
            const Icon = tierIcon(t.tier);
            return (
              <div key={t.topicId} className="home-mastery__row">
                <span className="home-mastery__icon" style={{ background: tier.bg, color: tier.fg }}>
                  <Icon size={13} aria-hidden="true" />
                </span>
                <span className="home-mastery__name">{t.name}</span>
                <span className="home-mastery__bar" aria-hidden="true">
                  <span style={{ width: `${t.pct}%`, background: tier.fg }} />
                </span>
                <span className="home-mastery__pct">{t.pct}%</span>
              </div>
            );
          })}
        </div>
      )}
      <span className="home-mastery__legend">Bronze ≥ 50% · prata ≥ 70% · ouro ≥ 85% com todas as aulas concluídas.</span>
    </>
  );
}

export default Home;
